/**
 * Vue « consultation » d'un bloc V2 (mode par défaut, sans formulaire).
 * Affiche : icône type (tooltip texte) + contenu markdown rendu avec KaTeX +
 * actions (ordre, éditer, dupliquer, supprimer) + pills toggle pour les
 * enfants éventuels. Le titre éventuel d'un bloc Énoncé est intégré au
 * markdown sous forme de titre H3 (préfixe `## …`).
 */

import { useState, type ReactNode } from 'react';
import {
  BookOpen,
  CheckCircle2,
  HelpCircle,
  Info,
  Lightbulb,
  Search,
  StickyNote,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import { cn, Button } from '@jeraisonne/ui';
import { MarkdownMath } from '@jeraisonne/ui';
import { VisualBlocksRenderer } from '@jeraisonne/ui';
import type { ExerciseBlock, ExerciseBlockType } from './types';

const TYPE_META: Record<ExerciseBlockType, { label: string; Icon: LucideIcon; color: string }> = {
  statement: { label: 'Énoncé', Icon: BookOpen, color: 'text-blue-700 bg-blue-50 ring-blue-200' },
  statement_question: {
    label: 'Question',
    Icon: HelpCircle,
    color: 'text-indigo-700 bg-indigo-50 ring-indigo-200',
  },
  correction: {
    label: 'Corrigé',
    Icon: CheckCircle2,
    color: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  },
  distractor: {
    label: 'Distracteur',
    Icon: XCircle,
    color: 'text-amber-700 bg-amber-50 ring-amber-200',
  },
  explanation: {
    label: 'Explication',
    Icon: Info,
    color: 'text-violet-700 bg-violet-50 ring-violet-200',
  },
  hint: { label: 'Indice', Icon: Lightbulb, color: 'text-pink-700 bg-pink-50 ring-pink-200' },
  note: {
    label: 'Note tuteur',
    Icon: StickyNote,
    color: 'text-slate-600 bg-slate-100 ring-slate-300',
  },
  clue_hunt: {
    label: 'Chasse aux indices',
    Icon: Search,
    color: 'text-cyan-700 bg-cyan-50 ring-cyan-200',
  },
};

const BORDER_COLOR: Record<ExerciseBlockType, string> = {
  statement: 'border-blue-200',
  statement_question: 'border-indigo-200',
  correction: 'border-emerald-200',
  distractor: 'border-amber-200',
  explanation: 'border-violet-200',
  hint: 'border-pink-200',
  note: 'border-slate-300',
  clue_hunt: 'border-cyan-200',
};

/**
 * Premier champ texte non vide parmi `keys`. Tolère les exos DAG/seed qui
 * stockent le texte dans `markdown` au lieu des clés canoniques (cf. CLAUDE.md,
 * piège "Tolérance de champs" côté buildLiveSteps).
 */
function firstStr(d: Record<string, string | undefined>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = d[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '';
}

/**
 * Construit la représentation markdown d'un bloc pour l'affichage en mode
 * consultation. Le titre (statement) est intégré comme heading. Les champs
 * secondaires utiles à l'aperçu (sub_statement_md, answer_md, feedback_md)
 * sont concaténés.
 */
function buildBlockMarkdown(block: ExerciseBlock): string {
  const d = block.data as Record<string, string | undefined>;
  switch (block.block_type) {
    case 'statement': {
      const title = d.title ? `## ${d.title}\n\n` : '';
      return title + firstStr(d, ['content_md', 'markdown']);
    }
    case 'statement_question': {
      const main = firstStr(d, ['prompt_md', 'markdown']);
      const sub = d.sub_statement_md ? `\n\n${d.sub_statement_md}` : '';
      return main + sub;
    }
    case 'correction': {
      const expl = firstStr(d, ['explanation_md', 'markdown']);
      const ans = firstStr(d, ['answer_md', 'label_md']);
      const mode = d.answer_mode === 'multiple' ? '\n\n_Plusieurs réponses possibles_' : '';
      return expl + (ans ? `\n\n**→ Réponse :** ${ans}` : '') + mode;
    }
    case 'distractor': {
      const lab = firstStr(d, ['label_md', 'answer_md', 'markdown']);
      const correct = (block.data as { is_correct?: boolean }).is_correct === true;
      const tag = correct ? ' _(aussi correct)_' : '';
      const fb = d.feedback_md ? `\n\n_${d.feedback_md}_` : '';
      return lab + tag + fb;
    }
    case 'hint':
    case 'explanation':
    case 'note':
      return firstStr(d, ['body_md', 'markdown']);
    case 'clue_hunt': {
      const intro = d.intro_md ?? '';
      const clues = (block.data as { clues?: Array<{ revealed_md?: string }> }).clues ?? [];
      const list = clues
        .map((c, i) => `${i + 1}. ${c.revealed_md?.trim() || '(indice non défini)'}`)
        .join('\n');
      return `${intro}\n\n${list}`;
    }
  }
}

export interface ChildToggle {
  key: string;
  Icon: LucideIcon;
  count: number;
  ariaLabel: string; // ex. "3 corrigés" (lu par les lecteurs d'écran + tooltip)
  expanded: boolean;
  onToggle: () => void;
  onAdd?: () => void;
}

export interface BlockCardProps {
  block: ExerciseBlock;
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  childToggles?: ChildToggle[];
  /** Nombre de steps de leçon liés à ce bloc (corrigé/distracteur). Badge read-only. */
  lessonLinkCount?: number;
  /** Outils de dictée ciblée (rendu sous la barre d'actions, en mode lecture). */
  voiceTools?: ReactNode;
  /** Pastilles d'état toujours visibles (ex. feuille de brouillon d'une question). */
  badges?: ReactNode;
}

export function BlockCard(props: BlockCardProps) {
  const {
    block,
    isFirst,
    isLast,
    isDisabled,
    onMoveUp,
    onMoveDown,
    onEdit,
    onDuplicate,
    onDelete,
    childToggles = [],
    lessonLinkCount = 0,
    voiceTools,
    badges,
  } = props;
  const meta = TYPE_META[block.block_type];
  const Icon = meta.Icon;
  const markdown = buildBlockMarkdown(block);
  const hasContent = markdown.trim().length > 0;
  // data.visuals : tableau de visuels V1 hérités ou créés par le pipeline.
  // Présent sur les types qui acceptent des visuels (statement, question, correction…).
  const visuals = (block.data as { visuals?: unknown }).visuals;
  const visualBlocks =
    Array.isArray(visuals) && visuals.length > 0 ? (visuals as ExerciseVisualBlock[]) : null;
  // Mode "minimal" par défaut : on n'affiche que l'icône + le contenu.
  // Cliquer sur la carte révèle/masque la barre d'actions et les pills toggle.
  const [showActions, setShowActions] = useState(false);

  // Stoppe la propagation pour que les clics sur les boutons d'action ou les
  // pills toggle ne re-toggle pas l'état showActions de la carte parente.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Compteurs visibles dans la gouttière (corrigés, indices, commentaires…) :
  // uniquement ceux dont le nombre est > 0. Toujours affichés (même carte
  // repliée) pour repérer d'un coup d'œil ce qui est attaché à la question.
  const gutterToggles = childToggles.filter((t) => t.count > 0);

  return (
    <article
      data-testid={`block-${block.id}`}
      data-mode="view"
      data-actions-visible={showActions}
      onClick={() => setShowActions((v) => !v)}
      className={cn(
        'group relative flex cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:border-slate-400',
        BORDER_COLOR[block.block_type],
        showActions && 'ring-1 ring-slate-300',
      )}
    >
      {/* Gouttière latérale : icône du type en haut, compteurs (corrigés /
          indices / commentaires) alignés en bas — fond coloré sur toute la hauteur. */}
      <div className={cn('flex w-10 shrink-0 flex-col items-center py-3 sm:w-12', meta.color)}>
        <span title={meta.label} aria-label={meta.label}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        {gutterToggles.length > 0 ? (
          <div className="mt-auto flex flex-col items-center gap-1.5 pt-3" onClick={stop}>
            {gutterToggles.map((t) => {
              const ToggleIcon = t.Icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={t.onToggle}
                  disabled={isDisabled}
                  aria-expanded={t.expanded}
                  aria-label={t.ariaLabel}
                  title={t.ariaLabel}
                  className={cn(
                    'inline-flex flex-col items-center rounded-md px-1 py-0.5 transition-colors',
                    t.expanded
                      ? 'bg-white/80 text-slate-800 ring-1 ring-slate-300'
                      : 'text-slate-600 hover:bg-white/60',
                  )}
                >
                  <ToggleIcon className="h-3.5 w-3.5" aria-hidden />
                  <span className="text-[10px] font-semibold tabular-nums leading-none">
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Contenu : markdown + barre unique en bas (toggles enfants + actions) */}
      <div className="flex min-w-0 flex-1 flex-col p-3">
        {hasContent ? (
          <MarkdownMath className="text-sm text-slate-800">{markdown}</MarkdownMath>
        ) : (
          <p className="text-sm italic text-slate-400">(vide)</p>
        )}

        {visualBlocks ? (
          <div className="mt-3" onClick={stop}>
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              Éléments visuels
            </p>
            <VisualBlocksRenderer blocks={visualBlocks} />
          </div>
        ) : null}

        {lessonLinkCount > 0 ? (
          <div className="mt-2">
            <span
              className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800"
              title="Step(s) de leçon liés à ce bloc"
            >
              🔗 {lessonLinkCount} leçon{lessonLinkCount > 1 ? 's' : ''} liée
              {lessonLinkCount > 1 ? 's' : ''}
            </span>
          </div>
        ) : null}

        {badges ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 empty:hidden">{badges}</div>
        ) : null}

        {showActions ? (
          <>
            <div
              className="mt-3 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2"
              onClick={stop}
            >
              {/* Pills toggle des sous-contenus (à gauche) */}
              {childToggles.map((t) => {
                const ToggleIcon = t.Icon;
                return (
                  <span key={t.key} className="inline-flex items-center">
                    <button
                      type="button"
                      onClick={t.onToggle}
                      disabled={isDisabled}
                      className={cn(
                        'inline-flex min-h-9 items-center gap-1 rounded-l-full border px-2 py-1 text-xs font-medium transition-colors',
                        t.expanded
                          ? 'border-slate-400 bg-slate-100 text-slate-800'
                          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
                        t.onAdd ? 'border-r-0' : 'rounded-r-full',
                      )}
                      aria-expanded={t.expanded}
                      aria-label={t.ariaLabel}
                      title={t.ariaLabel}
                    >
                      <span aria-hidden className="text-[10px]">
                        {t.expanded ? '▾' : '▸'}
                      </span>
                      <ToggleIcon className="h-3.5 w-3.5" aria-hidden />
                      <span className="tabular-nums">{t.count}</span>
                    </button>
                    {t.onAdd ? (
                      <button
                        type="button"
                        onClick={t.onAdd}
                        disabled={isDisabled}
                        className="inline-flex min-h-9 items-center rounded-r-full border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        aria-label={`Ajouter ${t.ariaLabel}`}
                        title="Ajouter"
                      >
                        +
                      </button>
                    ) : null}
                  </span>
                );
              })}

              {/* Actions (poussées à droite) */}
              <div className="ml-auto flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={onMoveUp}
                  disabled={isFirst || isDisabled}
                  className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  aria-label="Monter"
                  title="Monter"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={onMoveDown}
                  disabled={isLast || isDisabled}
                  className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  aria-label="Descendre"
                  title="Descendre"
                >
                  ▼
                </button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onEdit}
                  disabled={isDisabled}
                >
                  Éditer
                </Button>
                <button
                  type="button"
                  onClick={onDuplicate}
                  disabled={isDisabled}
                  className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  aria-label="Dupliquer"
                  title="Dupliquer"
                >
                  ⎘
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDisabled}
                  className="min-h-9 min-w-9 rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
                  aria-label="Supprimer"
                  title="Supprimer"
                >
                  ✕
                </button>
              </div>
            </div>
            {voiceTools ? (
              <div className="mt-2" onClick={stop}>
                {voiceTools}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </article>
  );
}
